/**
 * components/ProfileEditor.tsx
 * Edit risk configuration for a single profile
 *
 * Shows account default risk in header, strategies as accordions containing playbooks
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import { Card, CardBody, CardHeader, Input, Switch, Accordion, AccordionItem } from '@enfomentis/ui';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import { formatRiskDisplay } from '../utils/riskValidation';

/**
 * Risk profile type from draft
 */
type RiskProfile = NonNullable<OnboardingDraft['trading']['riskConfiguration']>['profiles'][number];
type RiskSpec = RiskProfile['accountDefault'];

/**
 * ProfileEditor props
 */
interface ProfileEditorProps {
  profile: RiskProfile;
  strategyName?: string;
  strategyTempId?: string;
  playbookNames: Map<string, string>; // tempId -> name
  onUpdateProfile: (updates: Partial<RiskProfile>) => void;
  onUpdateAccountDefault: (risk: RiskSpec) => void;
  onAddStrategyOverride: () => void;
  onEditStrategyOverride: (strategyTempId: string) => void;
  onRemoveStrategyOverride: (strategyTempId: string) => void;
  onAddPlaybookOverride: () => void;
  onEditPlaybookOverride: (playbookTempId: string) => void;
  onRemovePlaybookOverride: (playbookTempId: string) => void;
}

/**
 * ProfileEditor component
 */
export const ProfileEditor: React.FC<ProfileEditorProps> = ({
  profile,
  strategyName,
  strategyTempId,
  playbookNames,
  onUpdateAccountDefault,
  onUpdateProfile
}) => {
  // Get strategy override for the current strategy
  const strategyOverride = strategyTempId
    ? profile.strategyOverrides?.find(o => o.strategyTempId === strategyTempId)
    : undefined;

  // Helper to get effective risk for a playbook (cascade logic)
  const getPlaybookEffectiveRisk = (playbookTempId: string): RiskSpec => {
    const playbookOverride = profile.playbookOverrides?.find(o => o.playbookTempId === playbookTempId);
    if (playbookOverride) return playbookOverride.risk;
    if (strategyOverride) return strategyOverride.risk;
    return profile.accountDefault;
  };

  // Toggle strategy override
  const handleToggleStrategyOverride = (enabled: boolean) => {
    if (!strategyTempId) return;

    if (enabled) {
      // Add strategy override
      const newOverride = {
        strategyTempId,
        risk: { ...profile.accountDefault }
      };
      const strategyOverrides = [...(profile.strategyOverrides || []), newOverride];
      onUpdateProfile({ strategyOverrides });
    } else {
      // Remove strategy override
      const filtered = profile.strategyOverrides?.filter(o => o.strategyTempId !== strategyTempId);
      if (filtered) {
        onUpdateProfile({ strategyOverrides: filtered });
      }
    }
  };

  // Update strategy override risk
  const handleUpdateStrategyRisk = (risk: RiskSpec) => {
    if (!strategyTempId || !strategyOverride) return;

    const updated = profile.strategyOverrides?.map(o =>
      o.strategyTempId === strategyTempId ? { ...o, risk } : o
    );
    if (updated) {
      onUpdateProfile({ strategyOverrides: updated });
    }
  };

  // Toggle playbook override
  const handleTogglePlaybookOverride = (playbookTempId: string, enabled: boolean) => {
    if (enabled) {
      // Add playbook override
      const effectiveRisk = strategyOverride ? strategyOverride.risk : profile.accountDefault;
      const newOverride = {
        playbookTempId,
        risk: { ...effectiveRisk }
      };
      const playbookOverrides = [...(profile.playbookOverrides || []), newOverride];
      onUpdateProfile({ playbookOverrides });
    } else {
      // Remove playbook override
      const filtered = profile.playbookOverrides?.filter(o => o.playbookTempId !== playbookTempId);
      if (filtered) {
        onUpdateProfile({ playbookOverrides: filtered });
      }
    }
  };

  // Update playbook override risk
  const handleUpdatePlaybookRisk = (playbookTempId: string, risk: RiskSpec) => {
    const playbookOverride = profile.playbookOverrides?.find(o => o.playbookTempId === playbookTempId);
    if (!playbookOverride) return;

    const updated = profile.playbookOverrides?.map(o =>
      o.playbookTempId === playbookTempId ? { ...o, risk } : o
    );
    if (updated) {
      onUpdateProfile({ playbookOverrides: updated });
    }
  };

  return (
    <div className="space-y-6">
      {/* Risk Configuration Hierarchy */}
      <Card shadow="sm">
        {/* Account Default Risk - Card Header */}
        <CardHeader>
          <div className="w-full">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 ml-auto">
                <Input
                  type="number"
                  label="Account Risk"
                  labelPlacement='outside-left'
                  value={profile.accountDefault.type === 'percentage'
                    ? (profile.accountDefault.value * 100).toString()
                    : (profile.accountDefault.value / 100).toString()
                  }
                  onChange={(e) => {
                    const inputValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                    if (!isNaN(inputValue)) {
                      onUpdateAccountDefault({
                        ...profile.accountDefault,
                        value: profile.accountDefault.type === 'percentage'
                          ? inputValue / 100
                          : Math.round(inputValue * 100)
                      });
                    }
                  }}
                  min="0"
                  max={profile.accountDefault.type === 'percentage' ? '100' : undefined}
                  classNames={{
                    label:"font-semibold text-lg",
                    input: "text-right mr-2"
                  }}
                  size="sm"
                />

                <Switch
                  isSelected={profile.accountDefault.type === 'fixed'}
                  onValueChange={(isFixed) => {
                    const newType = isFixed ? 'fixed' : 'percentage';
                    onUpdateAccountDefault({
                      type: newType,
                      value: newType === 'percentage' ? 0.01 : 10000 // 1% or $100
                    });
                  }}
                  size="lg"
                  color="primary"
                  thumbIcon={({ isSelected, className }) => (
                    <span className={className}>
                      {isSelected ? '$' : '%'}
                    </span>
                  )}
                />
              </div>
            </div>
          </div>
        </CardHeader>

        {/* Strategy Accordion containing Playbooks */}
        <CardBody className="pt-0">
          {strategyName && strategyTempId ? (
            <Accordion variant="bordered">
              <AccordionItem
                key={strategyTempId}
                aria-label={strategyName}
                title={<span className="font-medium">{strategyName}</span>}
              >
                {/* Strategy Risk Settings Card */}
                <Card shadow="none" radius="none" className="mb-3">
                  <CardBody className="pt-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <Switch
                        isSelected={!!strategyOverride}
                        onValueChange={handleToggleStrategyOverride}
                        size="sm"
                      >
                        <span className="text-sm">Override Account Risk</span>
                      </Switch>

                      <div className="flex items-center gap-3 sm:ml-auto">
                        {strategyOverride ? (
                          <>
                            <Input
                              type="number"
                              label="Strategy Risk"
                              labelPlacement='outside-left'
                              value={strategyOverride.risk.type === 'percentage'
                                ? (strategyOverride.risk.value * 100).toString()
                                : (strategyOverride.risk.value / 100).toString()
                              }
                              onChange={(e) => {
                                const inputValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                if (!isNaN(inputValue)) {
                                  handleUpdateStrategyRisk({
                                    ...strategyOverride.risk,
                                    value: strategyOverride.risk.type === 'percentage'
                                      ? inputValue / 100
                                      : Math.round(inputValue * 100)
                                  });
                                }
                              }}
                              min="0"
                              max={strategyOverride.risk.type === 'percentage' ? '100' : undefined}
                              classNames={{
                                label:"font-semibold text-sm",
                                input: "text-right mr-2"
                              }}
                              size="sm"
                            />

                            <Switch
                              isSelected={strategyOverride.risk.type === 'fixed'}
                              onValueChange={(isFixed) => {
                                const newType = isFixed ? 'fixed' : 'percentage';
                                handleUpdateStrategyRisk({
                                  type: newType,
                                  value: newType === 'percentage' ? 0.01 : 10000
                                });
                              }}
                              size="lg"
                              color="primary"
                              thumbIcon={({ isSelected, className }) => (
                                <span className={className}>
                                  {isSelected ? '$' : '%'}
                                </span>
                              )}
                            />
                          </>
                        ) : (
                          <div className="text-sm text-text-secondary italic">
                            Using Account Default: {formatRiskDisplay(profile.accountDefault)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Playbooks Card */}
                <Card shadow="none">
                  <CardHeader className="text-sm font-semibold">
                    Playbook Risk Settings
                  </CardHeader>
                  <CardBody className="pt-0">
                    <div className="space-y-3">
                      {Array.from(playbookNames.entries()).map(([playbookTempId, playbookName]) => {
                        const playbookOverride = profile.playbookOverrides?.find(o => o.playbookTempId === playbookTempId);
                        const effectiveRisk = getPlaybookEffectiveRisk(playbookTempId);

                        return (
                          <div key={playbookTempId} className="p-3 bg-surface-50 rounded-lg">
                            <div className="font-medium mb-2 text-sm">{playbookName}</div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                              <Switch
                                isSelected={!!playbookOverride}
                                onValueChange={(enabled) => handleTogglePlaybookOverride(playbookTempId, enabled)}
                                size="sm"
                              >
                                <span className="text-sm">
                                  {strategyOverride ? 'Override Strategy Risk' : 'Override Account Risk'}
                                </span>
                              </Switch>

                              <div className="flex items-center gap-3 sm:ml-auto">
                                {playbookOverride ? (
                                  <>
                                    <Input
                                      type="number"
                                      label="Playbook Risk"
                                      labelPlacement='outside-left'
                                      value={playbookOverride.risk.type === 'percentage'
                                        ? (playbookOverride.risk.value * 100).toString()
                                        : (playbookOverride.risk.value / 100).toString()
                                      }
                                      onChange={(e) => {
                                        const inputValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                        if (!isNaN(inputValue)) {
                                          handleUpdatePlaybookRisk(playbookTempId, {
                                            ...playbookOverride.risk,
                                            value: playbookOverride.risk.type === 'percentage'
                                              ? inputValue / 100
                                              : Math.round(inputValue * 100)
                                          });
                                        }
                                      }}
                                      min="0"
                                      max={playbookOverride.risk.type === 'percentage' ? '100' : undefined}
                                      classNames={{
                                        label:"text-sm",
                                        input: "text-right mr-2"
                                      }}
                                      size="sm"
                                    />

                                    <Switch
                                      isSelected={playbookOverride.risk.type === 'fixed'}
                                      onValueChange={(isFixed) => {
                                        const newType = isFixed ? 'fixed' : 'percentage';
                                        handleUpdatePlaybookRisk(playbookTempId, {
                                          type: newType,
                                          value: newType === 'percentage' ? 0.01 : 10000
                                        });
                                      }}
                                      size="lg"
                                      color="primary"
                                      thumbIcon={({ isSelected, className }) => (
                                        <span className={className}>
                                          {isSelected ? '$' : '%'}
                                        </span>
                                      )}
                                    />
                                  </>
                                ) : (
                                  <div className="text-sm text-text-secondary italic">
                                    Using {strategyOverride ? 'Strategy' : 'Account Default'}: {formatRiskDisplay(effectiveRisk)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>
              </AccordionItem>
            </Accordion>
          ) : (
            <p className="text-sm text-text-secondary italic p-4">
              No strategy configured yet. Complete the Strategy Definition step to configure strategy-level risk.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
